/**
 * @file useChefAuth.ts
 * @description Hook managing kitchen operator authentication, role authorization, and venue assignment.
 * 
 * ROLE ENFORCEMENT CONTRACT:
 * Only authorized chef & kitchen roles ('CHEF', 'OWNER', 'MANAGER', 'KITCHEN') are permitted access.
 * Any other roles, invalid credentials, or unauthorized accounts are rejected with "Account doesn't exist".
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../config/supabase.config';

export interface ChefVenue {
  id: string;
  name: string;
  slug: string;
  role: string;
}

const ALLOWED_CHEF_ROLES = ['CHEF', 'OWNER', 'MANAGER', 'KITCHEN'];

// In-memory cache for chef venue memberships (5-minute TTL) to prevent repeated queries
const chefVenuesCache = new Map<string, { venues: ChefVenue[]; timestamp: number }>();
const CHEF_VENUES_CACHE_TTL_MS = 5 * 60 * 1000;

export const useChefAuth = () => {
  const [sessionUser, setSessionUser] = useState<any | null>(null);
  const [venues, setVenues] = useState<ChefVenue[]>([]);
  const [activeVenue, setActiveVenue] = useState<ChefVenue | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const isProcessingAuthRef = useRef<boolean>(false);

  const loadUserVenues = useCallback(async (userId: string): Promise<ChefVenue[]> => {
    // Check in-memory cache first to avoid re-querying venue_members
    const cached = chefVenuesCache.get(userId);
    if (cached && Date.now() - cached.timestamp < CHEF_VENUES_CACHE_TTL_MS) {
      setVenues(cached.venues);
      setActiveVenue(cached.venues[0]);
      return cached.venues;
    }

    // Retrieves venue memberships using the consolidated server-side RPC (SECURITY DEFINER)
    // to bypass table-level grant restrictions and joined-relation permission issues.
    let rawMemberships: any[] | null = null;
    let fetchError: any = null;

    try {
      const { data: bootstrap, error: bError } = await supabase.rpc('get_venue_admin_bootstrap_state');
      if (!bError && bootstrap && Array.isArray(bootstrap.venue_memberships)) {
        rawMemberships = bootstrap.venue_memberships;
      } else if (bError) {
        fetchError = bError;
      }
    } catch (rpcErr) {
      fetchError = rpcErr;
    }

    // Fallback path: Queries the venue_members relation directly when the bootstrap RPC is unavailable
    if (!rawMemberships) {
      const { data, error: vError } = await supabase
        .from('venue_members')
        .select(`
          venue_id,
          role,
          venues (
            id,
            name,
            slug
          )
        `)
        .eq('user_id', userId);

      if (vError) {
        fetchError = vError;
      } else {
        rawMemberships = data;
        fetchError = null;
      }
    }

    if (fetchError && !rawMemberships) {
      console.error('[useChefAuth] Failed to load venue memberships:', fetchError);
      // Differentiate transient gateway / connection timeouts from authorization errors
      const status = (fetchError as any)?.status || (fetchError as any)?.statusCode;
      const isTransient = status === 504 || status === 502 || status === 503 || status === 500 ||
        /timeout|gateway|network|failed to fetch/i.test(fetchError.message || '');
      if (isTransient) {
        throw new Error('Connection timeout or gateway issue. Please retry.');
      }
      throw new Error("Account doesn't exist");
    }

    const authorizedVenues: ChefVenue[] = [];
    for (const row of (rawMemberships || []) as any[]) {
      const role = String(row.role || '').toUpperCase();
      const venueObj = Array.isArray(row.venues) ? row.venues[0] : row.venues;
      if (ALLOWED_CHEF_ROLES.includes(role) && venueObj) {
        authorizedVenues.push({
          id: venueObj.id,
          name: venueObj.name,
          slug: venueObj.slug,
          role,
        });
      }
    }

    if (authorizedVenues.length === 0) {
      // User has no chef/kitchen role authorized for any venue
      await supabase.auth.signOut();
      setSessionUser(null);
      setVenues([]);
      setActiveVenue(null);
      chefVenuesCache.delete(userId);
      throw new Error("Account doesn't exist");
    }

    chefVenuesCache.set(userId, { venues: authorizedVenues, timestamp: Date.now() });
    setVenues(authorizedVenues);
    setActiveVenue(authorizedVenues[0]);
    return authorizedVenues;
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      try {
        setIsLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          currentUserIdRef.current = session.user.id;
          if (isMounted) setSessionUser(session.user);
          await loadUserVenues(session.user.id);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message || "Account doesn't exist");
          // Only clear session user when access is rejected, preserving session during transient gateway timeouts
          const isTransient = /timeout|gateway|retry|fetch/i.test(err?.message || '');
          if (!isTransient) {
            setSessionUser(null);
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session?.user) {
        currentUserIdRef.current = null;
        if (isMounted) {
          setSessionUser(null);
          setVenues([]);
          setActiveVenue(null);
        }
        return;
      }

      if (session?.user) {
        // Prevents redundant requests if login is actively running or token refresh with same user
        if (isProcessingAuthRef.current) return;

        if (event === 'TOKEN_REFRESHED' && currentUserIdRef.current === session.user.id) {
          if (isMounted) setSessionUser(session.user);
          return;
        }

        currentUserIdRef.current = session.user.id;
        try {
          if (isMounted) setSessionUser(session.user);
          await loadUserVenues(session.user.id);
        } catch (err: any) {
          if (isMounted) {
            const isTransient = /timeout|gateway|retry|fetch/i.test(err?.message || '');
            if (!isTransient) {
              setSessionUser(null);
              setVenues([]);
              setActiveVenue(null);
            }
          }
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [loadUserVenues]);

  const login = async (email: string, pass: string) => {
    setError(null);
    isProcessingAuthRef.current = true;
    try {
      const { data, error: lError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: pass,
      });

      if (lError || !data?.user) {
        throw new Error("Account doesn't exist");
      }

      currentUserIdRef.current = data.user.id;
      await loadUserVenues(data.user.id);
      setSessionUser(data.user);
      return data;
    } catch (err: any) {
      const isTransient = /timeout|gateway|retry|fetch/i.test(err?.message || '');
      if (!isTransient) {
        await supabase.auth.signOut();
        setSessionUser(null);
      }
      throw err;
    } finally {
      isProcessingAuthRef.current = false;
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setSessionUser(null);
    setVenues([]);
    setActiveVenue(null);
  };

  return {
    sessionUser,
    venues,
    activeVenue,
    setActiveVenue,
    isLoading,
    error,
    login,
    logout,
  };
};
